import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast, Toaster } from "sonner";
import { captureAttributionFromUrl, getAttribution, getVisitorName } from "@/attribution";
import { useSiteContent, buildWhatsAppUrl } from "@/content";
import {
  HONEYPOT_NAME,
  MIN_FILL_TIME_MS,
  markFormOpened,
  getFillTimeMs,
  botCheckFailed,
} from "@/antibot";
import {
  ArrowUpRight,
  Phone,
  Mail,
  MapPin,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Twitter,
  Apple,
  Play,
  Send,
  Leaf,
  Sparkles,
  Flame,
  ChefHat,
  Building2,
  HandCoins,
  ChevronRight,
  MessageCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_CHICKEN =
  "https://images.pexels.com/photos/13795311/pexels-photo-13795311.jpeg";
const HERO_BURGER =
  "https://images.pexels.com/photos/14678738/pexels-photo-14678738.jpeg";
const POTATO_WEDGES =
  "https://images.pexels.com/photos/3727184/pexels-photo-3727184.jpeg";
const CHEF_PORTRAIT =
  "https://images.pexels.com/photos/31065395/pexels-photo-31065395.jpeg";

const SWIGGY_URL = "https://www.swiggy.com/city/guwahati/woodies-snacks-rukminigaon-sarumotoria-rest1144727";
const ZOMATO_URL = "https://www.zomato.com/guwahati/woodies-snacks-1-hengerabari/order";

const OrderNowButton = ({ size = "md" }) => {
  const content = useSiteContent();
  const swiggy = content?.contact?.swiggy_url || SWIGGY_URL;
  const zomato = content?.contact?.zomato_url || ZOMATO_URL;
  const cls = size === "lg"
    ? "btn-brutal bg-[#FF4500] text-black font-display text-lg px-7 py-4 inline-flex items-center gap-3"
    : "btn-brutal bg-[#FF4500] text-black font-display text-sm md:text-base px-4 md:px-6 py-2.5 md:py-3 inline-flex items-center gap-2";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="nav-cta-order" className={cls}>
          ORDER NOW <ArrowUpRight size={size === "lg" ? 22 : 18} strokeWidth={2.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={12}
        className="w-72 bg-black border-[#FF4500] rounded-none p-3 text-white"
      >
        <div className="label-mono text-[#FFC107] mb-2">— PICK YOUR APP</div>
        <a
          href={swiggy}
          target="_blank"
          rel="noreferrer"
          data-testid="order-swiggy"
          className="flex items-center justify-between border border-white/15 hover:border-[#FF4500] hover:bg-[#FF4500] hover:text-black px-4 py-3 transition-colors group"
        >
          <span className="font-display text-xl" style={{ color: "#fc8019" }}>Swiggy</span>
          <ArrowUpRight size={18} className="text-white/40 group-hover:text-black" strokeWidth={2.5} />
        </a>
        <a
          href={zomato}
          target="_blank"
          rel="noreferrer"
          data-testid="order-zomato"
          className="mt-2 flex items-center justify-between border border-white/15 hover:border-[#FF4500] hover:bg-[#FF4500] hover:text-black px-4 py-3 transition-colors group"
        >
          <span className="font-display text-xl" style={{ color: "#e23744" }}>Zomato</span>
          <ArrowUpRight size={18} className="text-white/40 group-hover:text-black" strokeWidth={2.5} />
        </a>
        <div className="text-xs text-white/40 mt-3 text-center">
          Free delivery in {content?.contact?.address || "Guwahati"} area
        </div>
      </PopoverContent>
    </Popover>
  );
};

const WhatsAppButton = ({ size = "md" }) => {
  const content = useSiteContent();
  const url = buildWhatsAppUrl(content?.contact || {});
  const cls = size === "lg"
    ? "btn-brutal bg-[#25D366] text-black font-display text-lg px-6 py-4 inline-flex items-center gap-3"
    : "btn-brutal bg-[#25D366] text-black font-display text-sm md:text-base px-3 md:px-5 py-2.5 md:py-3 inline-flex items-center gap-2";
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-cta"
      aria-label="Chat on WhatsApp"
      className={cls}
    >
      <MessageCircle size={size === "lg" ? 22 : 18} strokeWidth={2.5} />
      <span className="hidden sm:inline">WHATSAPP</span>
    </a>
  );
};
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { label: "Story", href: "#story" },
    { label: "Menu", href: "/menu" },
    { label: "Flavors", href: "#flavors" },
    { label: "Impact", href: "#impact" },
    { label: "Join Us", href: "#join" },
  ];
  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 h-20 md:h-24 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-logo"
          className="font-display text-xl sm:text-2xl md:text-3xl tracking-tight inline-flex items-center gap-2 sm:gap-2.5 shrink-0"
        >
          <img src="/woodies-logo.svg" alt="Woodies" className="logo-on-dark h-11 w-11 sm:h-14 sm:w-14 md:h-16 md:w-16 hover:scale-105 transition-transform shrink-0" />
          <span className="inline">WOODIES<span className="text-[#FF4500]">.</span></span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className="label-mono hover:text-[#FF4500] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <WhatsAppButton />
          <OrderNowButton />
        </div>
      </div>
    </header>
  );
};

/* ============== HERO ============== */
const Hero = () => {
  const content = useSiteContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, -12]);
  const [visitorName, setVisitorName] = useState("");

  useEffect(() => {
    setVisitorName(getVisitorName());
  }, []);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] overflow-hidden pt-24 md:pt-28"
    >
      {/* Backdrop image */}
      <motion.div
        style={{ y, rotate }}
        className="absolute -right-20 md:-right-32 top-16 md:top-24 w-[80vw] md:w-[60vw] aspect-square pointer-events-none"
      >
        <div
          className="w-full h-full bg-cover bg-center opacity-70 mix-blend-screen"
          style={{
            backgroundImage: `url(${HERO_CHICKEN})`,
            maskImage:
              "radial-gradient(ellipse at center, black 50%, transparent 78%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 50%, transparent 78%)",
          }}
        />
      </motion.div>

      <div className="relative max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 pt-8 md:pt-16">
        {/* Personalized stamp visible only when ?src= in URL */}
        {visitorName && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="visitor-stamp"
            className="inline-flex items-center gap-2 bg-[#FFC107] text-black px-3 py-1.5 mb-6 font-mono text-xs tracking-widest uppercase"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
            HEY {visitorName.toUpperCase()} — WELCOME 👋
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8 md:mb-12"
        >
          <div className="h-px w-10 bg-[#FF4500]" />
          <span className="label-mono text-[#FF4500]">
            {content.hero.est_line}
          </span>
        </motion.div>

        <h1 className="font-display-tight text-[16vw] md:text-[12vw] lg:text-[10rem] leading-[0.85] uppercase">
          {(() => {
            const words = (content.hero.headline || "").trim().replace(/\.$/, "").split(/\s+/);
            const lead = words.slice(0, -1);
            const last = words[words.length - 1] || "";
            return (
              <>
                {lead.map((word, i) => (
                  <motion.span
                    key={`${word}-${i}`}
                    initial={{ opacity: 0, y: 80, skewY: 6 }}
                    animate={{ opacity: 1, y: 0, skewY: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 + i * 0.12 }}
                    className="block"
                  >
                    {word}
                    {i === 1 && (
                      <span className="inline-block ml-3 md:ml-6 align-middle text-[#FFC107]">
                        ✺
                      </span>
                    )}
                  </motion.span>
                ))}
                <motion.span
                  initial={{ opacity: 0, y: 80 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="block text-[#FF4500] italic"
                >
                  {last}.
                </motion.span>
              </>
            );
          })()}
        </h1>

        <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-6 md:gap-12 items-end">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="md:col-span-5 text-lg md:text-xl text-white/80 leading-relaxed max-w-md"
          >
            {content.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="md:col-span-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 md:justify-end"
          >
            <OrderNowButton size="lg" />
            <WhatsAppButton size="lg" />
            <a
              href="/menu"
              data-testid="hero-cta-menu"
              className="btn-brutal btn-brutal-yellow border-2 border-[#FFC107] text-[#FFC107] font-display text-lg px-7 py-4 inline-flex items-center gap-3"
            >
              SEE THE MENU <ChevronRight size={22} strokeWidth={3} />
            </a>
            <a
              href={`tel:${(content.contact?.phone || "").replace(/[^0-9+]/g, "")}`}
              data-testid="hero-cta-call"
              className="hidden lg:inline-flex btn-brutal border-2 border-white/30 hover:border-white text-white font-display text-lg px-7 py-4 items-center gap-3"
            >
              <Phone size={20} strokeWidth={2.5} /> {content.contact?.phone}
            </a>
          </motion.div>
        </div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-16 md:mt-24 border-t border-white/10 pt-6 md:pt-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
        >
          {(content.hero_stats || []).map((s) => (
            <div key={s.v} className="">
              <div className="font-display text-3xl md:text-5xl text-white">
                {s.k}
              </div>
              <div className="label-mono mt-2">{s.v}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ============== CITY MARQUEE ============== */
const CityMarquee = () => {
  const content = useSiteContent();
  const cities = content.cities && content.cities.length > 0 ? content.cities : ["DULIAJAN", "DIBRUGARH", "TINSUKIA", "GUWAHATI"];
  return (
    <div
      data-testid="cities-marquee"
      className="bg-[#FF4500] text-black border-y-2 border-black/20 py-4 md:py-5 overflow-hidden"
    >
      <Marquee speed={60} gradient={false} className="font-display">
        {Array.from({ length: 6 }).flatMap((_, i) =>
          cities.map((c, j) => (
            <span
              key={`${i}-${j}`}
              className="text-3xl md:text-5xl mx-8 md:mx-12 inline-flex items-center gap-8 md:gap-12 uppercase"
            >
              {c}
              <span className="text-[#FFC107]">✺</span>
            </span>
          ))
        )}
      </Marquee>
      <div className="text-center label-mono text-black/70 mt-3">
        FREE DELIVERY · FRESHLY COOKED · NEVER FROZEN
      </div>
    </div>
  );
};

/* ============== TRACTION STRIP ============== */
const TractionStrip = () => {
  const content = useSiteContent();
  const stats = content.traction_stats || [];
  return (
    <section
      data-testid="traction-strip"
      className="relative py-14 md:py-20 bg-[#0a0a0a] border-b border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-12">
          <div>
            <span className="label-mono text-[#FFC107]">— LIVE TRACTION</span>
            <h2 className="mt-3 font-display-tight text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]">
              22 months in.
              <br />
              <span className="text-[#FF4500]">Numbers don't lie.</span>
            </h2>
          </div>
          <a
            href="/deck"
            target="_blank"
            rel="noreferrer"
            data-testid="traction-deck-link"
            className="btn-brutal border-2 border-[#FFC107] text-[#FFC107] font-display px-5 py-3 inline-flex items-center gap-2 self-start"
          >
            VIEW INVESTOR DECK <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {stats.map((s, i) => (
            <div
              key={s.v}
              className={`p-5 md:p-7 border ${
                i === 0
                  ? "bg-[#FF4500] text-black border-[#FF4500]"
                  : "bg-[#0c0c0d] text-white border-white/10"
              }`}
            >
              <div className="font-display-tight text-4xl md:text-6xl leading-none">
                {s.k}
              </div>
              <div className="label-mono mt-3" style={{ color: i === 0 ? "rgba(0,0,0,0.7)" : undefined }}>
                {s.v}
              </div>
              <div className={`text-xs mt-1 ${i === 0 ? "text-black/60" : "text-white/40"}`}>
                {s.since}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============== STORY ============== */
const Story = () => {
  const items = [
    {
      icon: <ChefHat size={20} />,
      title: "Empowering HomeChefs",
      desc: "We hire home chefs to cook for you — financial independence for them, soulful food for you.",
    },
    {
      icon: <Flame size={20} />,
      title: "Freshly Cooked, Always",
      desc: "Every order is made fresh on demand. We serve zero frozen food. Period.",
    },
    {
      icon: <Leaf size={20} />,
      title: "Eco-Friendly Packaging",
      desc: "Crispy on the inside, conscious on the outside. Packaging that doesn't outlive the food.",
    },
    {
      icon: <Sparkles size={20} />,
      title: "Continuous Experimentation",
      desc: "We tweak, taste, and tune flavors obsessively. Perfection is the only KPI.",
    },
    {
      icon: <Building2 size={20} />,
      title: "Full-Stack In-House",
      desc: "Tech-powered R&D, manufacturing, sourcing, packaging, operations, customer loyalty and marketing — every layer owned end-to-end.",
    },
  ];
  return (
    <section
      id="story"
      data-testid="story-section"
      className="relative py-20 md:py-32"
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="label-mono text-[#FF4500]">/ 01 — OUR STORY</span>
            <h2 className="mt-4 font-display-tight text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9]">
              An online-first
              <br />
              kitchen with a<br />
              <span className="text-[#FFC107] italic">global appetite.</span>
            </h2>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-md">
              Woodies is a fast-growing QSR brand from India, on a mission to
              create an international fried chicken & snacks brand that's
              healthier, hyper-fresh, and built around the people who cook your
              food.
            </p>
            <div className="mt-8 relative h-[420px] md:h-[520px] overflow-hidden border border-white/10">
              <img
                src={CHEF_PORTRAIT}
                alt="HomeChef portrait"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="label-mono text-[#FFC107]">— OUR PEOPLE</div>
                <div className="font-display text-3xl md:text-4xl mt-1">
                  COOKED BY HOMECHEFS
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4 md:space-y-5">
            {items.map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group border border-white/10 hover:border-[#FF4500] bg-[#0f0f10] p-6 md:p-10 transition-colors"
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-3 text-[#FF4500] mb-3">
                      {it.icon}
                      <span className="label-mono">/ 0{i + 1}</span>
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">
                      {it.title}
                    </h3>
                    <p className="mt-3 text-white/70 max-w-xl">{it.desc}</p>
                  </div>
                  <ArrowUpRight
                    size={28}
                    className="text-white/30 group-hover:text-[#FF4500] group-hover:rotate-45 transition-all"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== MENU BENTO ============== */
const Menu = () => {
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative py-20 md:py-32 bg-[#080808]"
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-16">
          <div>
            <span className="label-mono text-[#FF4500]">
              / 02 — WHAT'S COOKING
            </span>
            <h2 className="mt-4 font-display-tight text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.9]">
              The Menu
              <br />
              <span className="text-[#FF4500]">Speaks Louder.</span>
            </h2>
          </div>
          <a
            href="/menu"
            data-testid="menu-full-link"
            className="btn-brutal border-2 border-white/30 hover:border-white text-white font-display px-6 py-3 inline-flex items-center gap-2 self-start"
          >
            FULL MENU <ArrowUpRight size={18} strokeWidth={2.5} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 auto-rows-[180px] md:auto-rows-[220px]">
          {/* Big Burger */}
          <div className="md:col-span-7 md:row-span-2 relative overflow-hidden border border-white/10 group">
            <img
              src={HERO_BURGER}
              alt="Jumbo Burger"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute top-6 left-6 right-6 flex justify-between">
              <span className="label-mono bg-[#FF4500] text-black px-3 py-1.5">
                SIGNATURE
              </span>
              <span className="label-mono bg-black/50 backdrop-blur px-3 py-1.5">
                FROM ₹149
              </span>
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="font-display-tight text-5xl md:text-7xl uppercase">
                Jumbo Burgers
              </h3>
              <p className="text-white/80 mt-2 max-w-md">
                Stacked tall, dripping right. Sourced fresh, never frozen.
              </p>
            </div>
          </div>

          {/* Chicken */}
          <div className="md:col-span-5 relative overflow-hidden border border-white/10 group">
            <img
              src={HERO_CHICKEN}
              alt="Crispy Fried Chicken"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF4500]/30 via-transparent to-black/70" />
            <div className="absolute bottom-5 left-5 right-5">
              <span className="label-mono text-[#FFC107]">— BESTSELLER</span>
              <h3 className="font-display-tight text-3xl md:text-5xl uppercase mt-1">
                Crispy Fried Chicken
              </h3>
            </div>
          </div>

          {/* Pips */}
          <div className="md:col-span-3 relative bg-[#FFC107] text-black p-6 flex flex-col justify-between border border-black/10">
            <div>
              <span className="label-mono !text-black/70">— PIPS</span>
              <h3 className="font-display-tight text-3xl md:text-4xl uppercase mt-2 leading-none">
                Sweet. Tangy. Tasty.
              </h3>
            </div>
            <div className="font-display text-2xl">7 FLAVORS ✺</div>
          </div>

          {/* Wedges */}
          <div className="md:col-span-2 relative overflow-hidden border border-white/10 group">
            <img
              src={POTATO_WEDGES}
              alt="Potato Wedges"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute bottom-3 left-3 right-3">
              <h3 className="font-display text-xl md:text-2xl uppercase">
                Wedges
              </h3>
            </div>
          </div>

          {/* Flavor kit */}
          <div className="md:col-span-7 relative bg-[#FF4500] text-black p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 border border-black/10">
            <div>
              <span className="label-mono !text-black/70">— LIMITED PACK</span>
              <h3 className="font-display-tight text-4xl md:text-6xl uppercase mt-2 leading-[0.9]">
                The Woodies
                <br />
                Flavor Kit.
              </h3>
              <p className="mt-3 max-w-md">
                All 7 signature flavors in one combo box. The full Woodies
                experience.
              </p>
            </div>
            <div className="font-display text-7xl md:text-8xl leading-none">
              ✺
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== FLAVOR TICKER ============== */
const FlavorStrip = () => {
  const flavors = [
    "CHEESEX",
    "TANGO TOMATO",
    "OREON ONION",
    "CREAMY",
    "SWEET",
    "TANGY",
    "TASTY",
    "NOSTALGIC",
    "CHATTY",
  ];
  return (
    <section
      id="flavors"
      data-testid="flavors-section"
      className="py-16 md:py-24 border-y border-white/10 flavor-strike"
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 mb-8 md:mb-12">
        <span className="label-mono text-[#FF4500]">/ 03 — THE FLAVORS</span>
        <h2 className="mt-3 font-display-tight text-4xl md:text-5xl lg:text-6xl uppercase">
          A spectrum your taste buds <br />
          haven't met yet.
        </h2>
      </div>
      <Marquee speed={40} gradient={false} className="font-display-tight">
        {Array.from({ length: 3 }).flatMap((_, i) =>
          flavors.map((f, j) => (
            <span
              key={`${i}-${j}`}
              className="text-6xl md:text-8xl lg:text-9xl uppercase mx-8 md:mx-14 inline-flex items-center gap-8 md:gap-14 text-white/90 hover:text-[#FF4500] transition-colors"
            >
              {f}
              <span className="text-[#FFC107] text-4xl md:text-6xl">✺</span>
            </span>
          ))
        )}
      </Marquee>
    </section>
  );
};

/* ============== IMPACT ============== */
const ImpactSection = () => {
  const pillars = [
    {
      title: "HYGIENE BY DESIGN",
      body: "Home kitchens are the cleanest kitchens most Indians know. Small-batch, cook-to-order, fresh oil every day — hygiene you can actually taste.",
      color: "#FFC107",
    },
    {
      title: "WOMEN, EMPOWERED",
      body: "Every homechef is a woman running her own business — first bank account, first UPI ID, first monthly income of her own. Independence, not charity.",
      color: "#FF4500",
    },
    {
      title: "NON-METRO JOB CREATION",
      body: "Duliajan · Tinsukia · Sibsagar · Dibrugarh — we build livelihoods in the towns most brands ignore. Talent stays home; families stay together.",
      color: "#22c55e",
    },
    {
      title: "LOCAL MONEY, LOCAL ECONOMY",
      body: "Chicken, oil, masala — all sourced locally. Every ₹100 spent at Woodies puts around ₹50 straight back into the neighbourhood.",
      color: "#a78bfa",
    },
    {
      title: "SCALE THAT SHARES THE UPSIDE",
      body: "More outlets = more chefs, more franchise partners, more suppliers, more jobs. Growth here is a rising tide, not a factory.",
      color: "#ffffff",
    },
    {
      title: "ALIGNED WITH 4 UN SDGs",
      body: "Gender Equality · Decent Work · Reduced Inequalities · Responsible Consumption. Impact by design, not by afterthought.",
      color: "#38bdf8",
    },
  ];
  return (
    <section
      id="impact"
      data-testid="impact-section"
      className="py-20 md:py-28 border-t border-white/10 bg-[#0a0a0a]"
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl">
          <span className="label-mono text-[#FFC107]">/ 04 — THE IMPACT</span>
          <h2 className="mt-3 font-display-tight text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]">
            Cooked with<br />
            <span className="text-[#FFC107]">impact.</span>
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
            Every Woodies outlet is more than fried chicken. It's income, dignity,
            jobs, hygiene and heritage — cooked into every plate.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              className="border border-white/10 bg-[#0c0c0d] p-6 md:p-8 hover:border-white/25 transition-colors group"
              data-testid={`impact-pillar-${i + 1}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="font-display text-3xl" style={{ color: p.color }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className="label-mono text-[10px] tracking-[2px]"
                  style={{ color: p.color }}
                >
                  — PILLAR
                </span>
              </div>
              <div className="font-display-tight text-2xl md:text-3xl uppercase leading-tight">
                {p.title}
              </div>
              <div className="text-white/70 text-sm md:text-base mt-4 leading-relaxed">
                {p.body}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 border-t border-white/10 pt-8 max-w-3xl">
          <p className="text-white/60 text-base md:text-lg leading-relaxed">
            <span className="text-white">Every outlet doesn't just serve chicken</span>
            {" "}— it creates <span className="text-[#FFC107]">4-6 women entrepreneurs</span>,
            keeps <span className="text-[#FFC107]">₹8-12 lakh/year</span> in the local
            economy, and puts a mother's cooking on a national stage.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ============== INTEREST FORMS ============== */
const FormField = ({ label, name, type = "text", value, onChange, required = true, testid, placeholder, min, max, pattern }) => (
  <label className="block">
    <div className="label-mono mb-2">{label}{required && " *"}</div>
    <Input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      min={min}
      max={max}
      pattern={pattern}
      placeholder={placeholder || label}
      data-testid={testid}
      className="rounded-none bg-black/40 border-white/20 text-white h-12 focus-visible:ring-[#FF4500]"
    />
  </label>
);

// Honeypot input — hidden from real humans, bots will autofill
const Honeypot = ({ value, onChange }) => (
  <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
    <label>
      Website
      <input
        type="text"
        name={HONEYPOT_NAME}
        value={value}
        onChange={onChange}
        tabIndex={-1}
        autoComplete="off"
      />
    </label>
  </div>
);

// Native select styled to match dark form
const SelectField = ({ label, name, value, onChange, options, required = true, testid, placeholder = "— select —" }) => (
  <label className="block">
    <div className="label-mono mb-2">{label}{required && " *"}</div>
    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full rounded-none bg-black/40 border border-white/20 text-white h-12 px-3 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
    >
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-black text-white">{o}</option>
      ))}
    </select>
  </label>
);

// Yes/No segmented control
const YesNoField = ({ label, name, value, onChange, required = true, testid }) => (
  <fieldset className="block">
    <legend className="label-mono mb-2">{label}{required && " *"}</legend>
    <div className="flex gap-3" data-testid={testid}>
      {["Yes", "No"].map((opt) => (
        <label key={opt} className={`flex-1 cursor-pointer text-center font-display tracking-wider py-3 border ${value === opt ? "bg-[#FF4500] text-black border-[#FF4500]" : "border-white/30 text-white/80 hover:border-white"}`}>
          <input
            type="radio"
            name={name}
            value={opt}
            checked={value === opt}
            onChange={onChange}
            required={required}
            className="sr-only"
            data-testid={`${testid}-${opt.toLowerCase()}`}
          />
          {opt.toUpperCase()}
        </label>
      ))}
    </div>
  </fieldset>
);

// Multi-checkbox group (returns array)
const MultiCheckField = ({ label, name, values, onToggle, options, required = true, testid }) => (
  <fieldset className="block">
    <legend className="label-mono mb-2">{label}{required && " *"}</legend>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            onClick={() => onToggle(name, opt)}
            data-testid={`${testid}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-4 py-2 border text-sm tracking-wide ${active ? "bg-[#FFC107] text-black border-[#FFC107]" : "bg-black/40 text-white/80 border-white/30 hover:border-white"}`}
          >
            {active ? "✓ " : ""}{opt}
          </button>
        );
      })}
    </div>
    {required && values.length === 0 && (
      <input
        tabIndex={-1}
        aria-hidden
        required
        value=""
        onChange={() => {}}
        className="opacity-0 h-0 w-0 absolute pointer-events-none"
      />
    )}
  </fieldset>
);

// Image picker → base64 (max 1MB after read; we just enforce <2MB raw before encode)
const PhotoField = ({ label, name, value, onFile, required = true, testid }) => {
  const handle = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(`${label}: max 2MB please`);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onFile(name, reader.result);
    reader.onerror = () => toast.error(`Could not read ${label}`);
    reader.readAsDataURL(file);
  };
  return (
    <label className="block">
      <div className="label-mono mb-2">{label}{required && " *"}</div>
      <div className={`border-2 border-dashed ${value ? "border-[#FFC107]" : "border-white/30"} bg-black/40 p-3 flex items-center gap-3`}>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handle}
          required={required && !value}
          data-testid={testid}
          className="text-sm text-white/80 file:mr-3 file:px-3 file:py-2 file:border-0 file:bg-white file:text-black file:font-display file:tracking-wider file:cursor-pointer"
        />
        {value && <span className="text-[#FFC107] text-xs font-mono">✓ uploaded</span>}
      </div>
    </label>
  );
};

const useForm = (initial) => {
  const [data, setData] = useState(initial);
  const onChange = (e) =>
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  return [data, onChange, setData];
};

// Hook: fill name from ?src= visitor slug, mark form opened (for fill-time anti-bot)
const useFormBootstrap = (setData, fields = ["name"]) => {
  useEffect(() => {
    markFormOpened();
    const visitor = getVisitorName();
    if (visitor) {
      setData((prev) => {
        const patch = {};
        fields.forEach((f) => {
          if (!prev[f]) patch[f] = visitor;
        });
        return Object.keys(patch).length ? { ...prev, ...patch } : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const InvestorForm = () => {
  const [data, onChange, setData] = useForm({
    name: "",
    email: "",
    phone: "",
    organization: "",
    investment_range: "",
    linkedin_url: "",
    message: "",
    [HONEYPOT_NAME]: "",
  });
  useFormBootstrap(setData);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const fillTimeMs = getFillTimeMs();
    const failed = botCheckFailed({ honeypot: data[HONEYPOT_NAME], fillTimeMs });
    if (failed) {
      toast.success("Thanks! Our team will reach out within 48h.");
      setData({ name: "", email: "", phone: "", organization: "", investment_range: "", linkedin_url: "", message: "", [HONEYPOT_NAME]: "" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/interest/investor`, { ...data, fill_time_ms: fillTimeMs, attribution: getAttribution() });
      toast.success("Thanks! Our team will reach out within 48h.");
      setData({ name: "", email: "", phone: "", organization: "", investment_range: "", linkedin_url: "", message: "", [HONEYPOT_NAME]: "" });
    } catch {
      toast.error("Something went wrong. Try again or call us.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="investor-form" className="grid md:grid-cols-2 gap-5">
      <Honeypot value={data[HONEYPOT_NAME]} onChange={onChange} />
      <div className="md:col-span-2 -mt-2 mb-2 border border-[#FFC107] bg-[#1a1305] p-4 md:p-5 grid sm:grid-cols-[1fr_auto_auto] gap-3 items-center">
        <div>
          <div className="label-mono text-[#FFC107]">— INVESTOR DECK</div>
          <div className="font-display text-xl md:text-2xl mt-1">Read the deck or book a call.</div>
          <div className="text-white/60 text-sm mt-1">12 slides · ₹50L ask · 30-min slots open.</div>
        </div>
        <a
          href="/deck"
          target="_blank"
          rel="noreferrer"
          data-testid="investor-deck-link"
          className="btn-brutal bg-[#FFC107] text-black font-display px-4 py-3 inline-flex items-center gap-2 whitespace-nowrap"
        >
          VIEW DECK <ArrowUpRight size={16} strokeWidth={2.5} />
        </a>
        <a
          href="https://calendly.com/team-woodiesfan/30min"
          target="_blank"
          rel="noreferrer"
          data-testid="investor-call-link"
          className="btn-brutal bg-[#FF4500] text-black font-display px-4 py-3 inline-flex items-center gap-2 whitespace-nowrap"
        >
          BOOK CALL <ArrowUpRight size={16} strokeWidth={2.5} />
        </a>
      </div>
      <FormField label="Full name" name="name" value={data.name} onChange={onChange} testid="investor-name" />
      <FormField label="Email" name="email" type="email" value={data.email} onChange={onChange} testid="investor-email" />
      <FormField label="Phone" name="phone" type="tel" value={data.phone} onChange={onChange} testid="investor-phone" />
      <FormField label="Organization" name="organization" value={data.organization} onChange={onChange} testid="investor-org" />
      <FormField label="Investment range (₹)" name="investment_range" value={data.investment_range} onChange={onChange} testid="investor-range" placeholder="e.g. 5L - 50L" />
      <FormField label="LinkedIn URL" name="linkedin_url" type="url" value={data.linkedin_url} onChange={onChange} testid="investor-linkedin" placeholder="https://linkedin.com/in/yourprofile" />
      <label className="block md:col-span-2">
        <div className="label-mono mb-2">Message *</div>
        <Textarea
          name="message"
          value={data.message}
          onChange={onChange}
          required
          rows={4}
          data-testid="investor-message"
          placeholder="Tell us about your interest"
          className="rounded-none bg-black/40 border-white/20 text-white focus-visible:ring-[#FF4500]"
        />
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid="investor-submit"
        className="btn-brutal bg-[#FF4500] text-black font-display text-lg px-8 py-4 inline-flex items-center gap-2 disabled:opacity-50 self-start"
      >
        {loading ? "SENDING..." : "INVEST IN WOODIES"} <Send size={18} strokeWidth={2.5} />
      </button>
    </form>
  );
};

const HOMECHEF_INITIAL = {
  // core (back-compat)
  name: "", email: "", phone: "", city: "", message: "",
  // jotform
  first_name: "", last_name: "",
  street1: "", street2: "", state: "", zip_code: "",
  age: "", gender: "",
  residence_type: "", marital_status: "", employment_status: "",
  languages_speak: [], languages_read: [],
  appliances: [],
  cloud_kitchen_ok: "", delivery_pickup_ok: "",
  hours_ok: "", salary_ok: "",
  cooking_experience: "",
  contract_ok: "", health_issues: "",
  start_date: "", call_back_at: "",
  kitchen_photo: "", selfie_photo: "", house_photo: "",
  [HONEYPOT_NAME]: "",
};

const HomeChefForm = () => {
  const [data, onChange, setData] = useForm(HOMECHEF_INITIAL);
  useFormBootstrap(setData, ["first_name"]);
  const [loading, setLoading] = useState(false);

  const onToggleMulti = (name, opt) => {
    setData((prev) => {
      const arr = prev[name] || [];
      return { ...prev, [name]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] };
    });
  };
  const onFile = (name, dataUrl) => setData((prev) => ({ ...prev, [name]: dataUrl }));

  const submit = async (e) => {
    e.preventDefault();
    // Multi-select & photo manual validation
    if (data.languages_speak.length === 0) return toast.error("Pick at least one language you can speak");
    if (data.languages_read.length === 0) return toast.error("Pick at least one language you can read");
    if (data.appliances.length === 0) return toast.error("Tell us which appliances you have");
    if (!data.kitchen_photo) return toast.error("Kitchen photo is required");
    if (!data.selfie_photo) return toast.error("Selfie photo is required");
    if (!data.house_photo) return toast.error("House front photo is required");

    const fillTimeMs = getFillTimeMs();
    const failed = botCheckFailed({ honeypot: data[HONEYPOT_NAME], fillTimeMs });
    if (failed) {
      toast.success("Welcome to the Woodies kitchen! We'll be in touch.");
      setData(HOMECHEF_INITIAL);
      return;
    }
    setLoading(true);
    try {
      const fullName = `${data.first_name} ${data.last_name}`.trim();
      await axios.post(`${API}/interest/homechef`, {
        ...data,
        name: fullName || data.name,
        fill_time_ms: fillTimeMs,
        attribution: getAttribution(),
      });
      toast.success("Welcome to the Woodies kitchen! We'll be in touch.");
      setData(HOMECHEF_INITIAL);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} data-testid="homechef-form" className="grid md:grid-cols-2 gap-5">
      <Honeypot value={data[HONEYPOT_NAME]} onChange={onChange} />

      {/* PERSONAL */}
      <div className="md:col-span-2 label-mono text-[#FFC107] mt-1">— PERSONAL DETAILS</div>
      <FormField label="First name" name="first_name" value={data.first_name} onChange={onChange} testid="homechef-first-name" />
      <FormField label="Last name" name="last_name" value={data.last_name} onChange={onChange} testid="homechef-last-name" />
      <FormField label="Email" name="email" type="email" value={data.email} onChange={onChange} testid="homechef-email" />
      <FormField label="Phone" name="phone" type="tel" value={data.phone} onChange={onChange} testid="homechef-phone" />
      <FormField label="Current age" name="age" type="number" min="18" max="80" value={data.age} onChange={onChange} testid="homechef-age" placeholder="Please enter a true value, we verify it" />
      <SelectField label="Gender" name="gender" value={data.gender} onChange={onChange} testid="homechef-gender" options={["Male", "Female", "Trans"]} />

      {/* ADDRESS */}
      <div className="md:col-span-2 label-mono text-[#FFC107] mt-3">— ADDRESS</div>
      <FormField label="Street address" name="street1" value={data.street1} onChange={onChange} testid="homechef-street1" />
      <FormField label="Street address line 2" name="street2" value={data.street2} onChange={onChange} testid="homechef-street2" required={false} />
      <FormField label="City" name="city" value={data.city} onChange={onChange} testid="homechef-city" />
      <FormField label="State / Province" name="state" value={data.state} onChange={onChange} testid="homechef-state" />
      <FormField label="Postal / Zip code" name="zip_code" value={data.zip_code} onChange={onChange} testid="homechef-zip" />

      {/* HOUSEHOLD */}
      <div className="md:col-span-2 label-mono text-[#FFC107] mt-3">— HOUSEHOLD</div>
      <SelectField label="Type of residence" name="residence_type" value={data.residence_type} onChange={onChange} testid="homechef-residence" options={["Rented", "Own home"]} />
      <SelectField label="Current status" name="marital_status" value={data.marital_status} onChange={onChange} testid="homechef-marital" options={["Married with no kids", "Married with kids", "Unmarried"]} />
      <SelectField label="Current employment status" name="employment_status" value={data.employment_status} onChange={onChange} testid="homechef-employment" options={["Housewife", "Service", "Business", "Freelance", "Student"]} />

      {/* LANGUAGES */}
      <div className="md:col-span-2"><MultiCheckField label="Languages you can speak" name="languages_speak" values={data.languages_speak} onToggle={onToggleMulti} testid="homechef-lang-speak" options={["English", "Hindi", "Assamese", "Bengali", "Other"]} /></div>
      <div className="md:col-span-2"><MultiCheckField label="Languages you can read" name="languages_read" values={data.languages_read} onToggle={onToggleMulti} testid="homechef-lang-read" options={["English", "Hindi", "Assamese", "Bengali", "Other"]} /></div>

      {/* KITCHEN */}
      <div className="md:col-span-2 label-mono text-[#FFC107] mt-3">— KITCHEN & WORK</div>
      <div className="md:col-span-2"><MultiCheckField label="Which of these do you have at home" name="appliances" values={data.appliances} onToggle={onToggleMulti} testid="homechef-appliances" options={["Fridge", "Gas stove", "Mixer grinder", "Oven"]} /></div>
      <YesNoField label="Will your house owner allow operating a cloud kitchen from your premises?" name="cloud_kitchen_ok" value={data.cloud_kitchen_ok} onChange={onChange} testid="homechef-cloudkitchen" />
      <YesNoField label="Will your house owner allow delivery boys to pick up orders from your premises?" name="delivery_pickup_ok" value={data.delivery_pickup_ok} onChange={onChange} testid="homechef-delivery" />
      <YesNoField label="Available 11:00 a.m. – 10:00 p.m. Monday to Sunday?" name="hours_ok" value={data.hours_ok} onChange={onChange} testid="homechef-hours" />
      <YesNoField label="Starting salary INR 10,000–12,000 / month + yearly bonus — okay?" name="salary_ok" value={data.salary_ok} onChange={onChange} testid="homechef-salary" />
      <div className="md:col-span-2"><SelectField label="Previous experience in cooking" name="cooking_experience" value={data.cooking_experience} onChange={onChange} testid="homechef-experience" options={[
        "Yes, I was a cook/chef at a restaurant before.",
        "No, the only cooking I have ever done is for my family as a housewife.",
        "Yes, I ran my own fast food business in the past.",
        "I cook for myself.",
      ]} /></div>
      <div className="md:col-span-2"><YesNoField label="OK with NDA on Woodies recipes & a non-compete clause?" name="contract_ok" value={data.contract_ok} onChange={onChange} testid="homechef-contract" /></div>
      <YesNoField label="Any health issues that prevent kitchen work under heavy orders?" name="health_issues" value={data.health_issues} onChange={onChange} testid="homechef-health" />
      <FormField label="When can you start working for Woodies?" name="start_date" type="date" value={data.start_date} onChange={onChange} testid="homechef-start" />

      {/* PHOTOS */}
      <div className="md:col-span-2 label-mono text-[#FFC107] mt-3">— PHOTO VERIFICATION (max 2MB each)</div>
      <PhotoField label="360° pan view of your kitchen" name="kitchen_photo" value={data.kitchen_photo} onFile={onFile} testid="homechef-photo-kitchen" />
      <PhotoField label="Clear selfie (passport size)" name="selfie_photo" value={data.selfie_photo} onFile={onFile} testid="homechef-photo-selfie" />
      <PhotoField label="Front view of your house from roadside" name="house_photo" value={data.house_photo} onFile={onFile} testid="homechef-photo-house" />

      {/* CALLBACK */}
      <div className="md:col-span-2 label-mono text-[#FFC107] mt-3">— CALL BACK</div>
      <FormField label="When can we call you?" name="call_back_at" type="datetime-local" value={data.call_back_at} onChange={onChange} testid="homechef-callback" />
      <label className="block">
        <div className="label-mono mb-2">Anything else (optional)</div>
        <Textarea
          name="message"
          value={data.message}
          onChange={onChange}
          rows={2}
          data-testid="homechef-message"
          className="rounded-none bg-black/40 border-white/20 text-white focus-visible:ring-[#FF4500]"
        />
      </label>

      <div className="md:col-span-2 text-white/50 text-xs font-mono mt-2">
        BY SUBMITTING, YOU CONFIRM ALL ANSWERS ARE TRUE. WE VERIFY EVERY DETAIL BEFORE ONBOARDING.
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="homechef-submit"
        className="btn-brutal bg-[#FFC107] text-black font-display text-lg px-8 py-4 inline-flex items-center gap-2 disabled:opacity-50 self-start md:col-span-2"
      >
        {loading ? "SENDING..." : "JOIN AS HOMECHEF"} <ChefHat size={18} strokeWidth={2.5} />
      </button>
    </form>
  );
};

const FranchiseeForm = () => {
  const [data, onChange, setData] = useForm({
    name: "", email: "", phone: "", location: "", capital: "", message: "",
    [HONEYPOT_NAME]: "",
  });
  useFormBootstrap(setData);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const fillTimeMs = getFillTimeMs();
    const failed = botCheckFailed({ honeypot: data[HONEYPOT_NAME], fillTimeMs });
    if (failed) {
      toast.success("Application received! Our franchise team will contact you.");
      setData({ name: "", email: "", phone: "", location: "", capital: "", message: "", [HONEYPOT_NAME]: "" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/interest/franchisee`, { ...data, fill_time_ms: fillTimeMs, attribution: getAttribution() });
      toast.success("Application received! Our franchise team will contact you.");
      setData({ name: "", email: "", phone: "", location: "", capital: "", message: "", [HONEYPOT_NAME]: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} data-testid="franchisee-form" className="grid md:grid-cols-2 gap-5">
      <Honeypot value={data[HONEYPOT_NAME]} onChange={onChange} />
      <FormField label="Full name" name="name" value={data.name} onChange={onChange} testid="franchisee-name" />
      <FormField label="Email" name="email" type="email" value={data.email} onChange={onChange} testid="franchisee-email" />
      <FormField label="Phone" name="phone" type="tel" value={data.phone} onChange={onChange} testid="franchisee-phone" />
      <FormField label="Preferred location" name="location" value={data.location} onChange={onChange} testid="franchisee-location" />
      <FormField label="Available capital (₹)" name="capital" value={data.capital} onChange={onChange} testid="franchisee-capital" placeholder="e.g. 15L - 50L" />
      <label className="block md:col-span-2">
        <div className="label-mono mb-2">Why do you want to franchise Woodies? *</div>
        <Textarea
          name="message"
          value={data.message}
          onChange={onChange}
          required
          rows={4}
          data-testid="franchisee-message"
          className="rounded-none bg-black/40 border-white/20 text-white focus-visible:ring-[#FF4500]"
        />
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid="franchisee-submit"
        className="btn-brutal bg-white text-black font-display text-lg px-8 py-4 inline-flex items-center gap-2 disabled:opacity-50 self-start"
      >
        {loading ? "SENDING..." : "OPEN A WOODIES"} <Building2 size={18} strokeWidth={2.5} />
      </button>
    </form>
  );
};

const Join = () => {
  return (
    <section
      id="join"
      data-testid="join-section"
      className="relative py-20 md:py-32"
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="label-mono text-[#FF4500]">/ 04 — JOIN US</span>
            <h2 className="mt-4 font-display-tight text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9]">
              Build the
              <br />
              <span className="text-[#FFC107] italic">Woodies</span>
              <br />
              tomorrow.
            </h2>
            <p className="mt-5 text-white/70 max-w-md">
              Three ways to be part of the journey — choose your tab and let's
              talk. Real humans reply within 48 hours.
            </p>
            <div className="mt-8 space-y-3">
              {[
                { icon: <HandCoins size={18} />, t: "Investor", d: "Back the next big QSR." },
                { icon: <ChefHat size={18} />, t: "HomeChef", d: "Cook with Woodies, earn with us." },
                { icon: <Building2 size={18} />, t: "Franchisee", d: "Bring Woodies to your city." },
              ].map((x) => (
                <div key={x.t} className="flex items-start gap-3 border-l-2 border-[#FF4500] pl-4">
                  <span className="text-[#FF4500] mt-0.5">{x.icon}</span>
                  <div>
                    <div className="font-display text-xl uppercase">{x.t}</div>
                    <div className="label-mono">{x.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8">
            <Tabs defaultValue="investor" className="w-full">
              <TabsList
                data-testid="join-tabs"
                className="w-full bg-transparent p-0 border border-white/10 rounded-none h-auto grid grid-cols-3"
              >
                {[
                  { v: "investor", l: "INVESTOR" },
                  { v: "homechef", l: "HOMECHEF" },
                  { v: "franchisee", l: "FRANCHISEE" },
                ].map((t) => (
                  <TabsTrigger
                    key={t.v}
                    value={t.v}
                    data-testid={`tab-${t.v}`}
                    className="rounded-none font-display text-base md:text-lg py-4 data-[state=active]:bg-[#FF4500] data-[state=active]:text-black data-[state=active]:shadow-none text-white/70 hover:text-white"
                  >
                    {t.l}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="border border-t-0 border-white/10 p-6 md:p-10 bg-[#0c0c0d]">
                <TabsContent value="investor" className="mt-0">
                  <InvestorForm />
                </TabsContent>
                <TabsContent value="homechef" className="mt-0">
                  <HomeChefForm />
                </TabsContent>
                <TabsContent value="franchisee" className="mt-0">
                  <FranchiseeForm />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== FOOTER ============== */
const Footer = () => {
  const content = useSiteContent();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/newsletter`, { email });
      setDone(true);
      setEmail("");
      toast.success("You're in. Welcome to the Woodies family.");
    } catch {
      toast.error("Subscription failed. Try again.");
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-white/10 pt-16 md:pt-24 pb-8"
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-5">
            <div className="font-display inline-flex items-center gap-4">
              <img src="/woodies-logo.svg" alt="Woodies" className="logo-on-dark h-20 md:h-24 w-20 md:w-24" />
              <span className="text-4xl md:text-5xl">WOODIES<span className="text-[#FF4500]">.</span></span>
            </div>
            <p className="mt-4 text-white/60 max-w-sm">
              Best Fried Friends Forever. Crafting India's next global QSR
              brand, one freshly cooked order at a time.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex border border-white/20 max-w-md">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                data-testid="newsletter-email"
                placeholder="your@email.com"
                className="flex-1 bg-transparent px-4 py-3 text-white placeholder:text-white/30 focus:outline-none"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                className="bg-[#FF4500] text-black font-display px-5 py-3 inline-flex items-center gap-2 hover:bg-[#FFC107] transition-colors"
              >
                {done ? "✓ DONE" : "SUBSCRIBE"} <Send size={16} />
              </button>
            </form>
            <div className="mt-4 label-mono">
              Get menu drops, new city launches & secret combos.
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="label-mono mb-4">Contact</div>
            <ul className="space-y-3">
              <li>
                <a href={`mailto:${content.contact?.email || "team@woodiesfan.com"}`} className="hover:text-[#FF4500] inline-flex items-center gap-2">
                  <Mail size={16} /> {content.contact?.email || "team@woodiesfan.com"}
                </a>
              </li>
              <li>
                <a href={`tel:${(content.contact?.phone || "").replace(/[^0-9+]/g, "")}`} className="hover:text-[#FF4500] inline-flex items-center gap-2">
                  <Phone size={16} /> {content.contact?.phone || "+91-6003700836"}
                </a>
              </li>
              <li>
                <a href={buildWhatsAppUrl(content.contact || {})} target="_blank" rel="noreferrer" className="hover:text-[#25D366] inline-flex items-center gap-2">
                  <MessageCircle size={16} /> WhatsApp us
                </a>
              </li>
              <li className="inline-flex items-center gap-2 text-white/70">
                <MapPin size={16} /> {content.contact?.address || "Guwahati, India"}
              </li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              {content.contact?.instagram_url && (
                <a href={content.contact.instagram_url} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] p-2.5"><Instagram size={18} /></a>
              )}
              {content.contact?.facebook_url && (
                <a href={content.contact.facebook_url} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] p-2.5"><Facebook size={18} /></a>
              )}
              {content.contact?.youtube_url && (
                <a href={content.contact.youtube_url} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="footer-youtube" className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] p-2.5"><Youtube size={18} /></a>
              )}
              {content.contact?.linkedin_url && (
                <a href={content.contact.linkedin_url} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin" className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] p-2.5"><Linkedin size={18} /></a>
              )}
              {content.contact?.twitter_url && (
                <a href={content.contact.twitter_url} target="_blank" rel="noreferrer" aria-label="Twitter / X" data-testid="footer-twitter" className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] p-2.5"><Twitter size={18} /></a>
              )}
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="label-mono mb-4">Get the app</div>
            <div className="space-y-3">
              <div className="border border-white/15 px-5 py-3.5 flex items-center justify-between hover:border-[#FFC107] transition-colors group cursor-not-allowed opacity-90" data-testid="store-googleplay">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 border border-white/20 group-hover:border-[#FFC107] flex items-center justify-center text-[#FFC107]">
                    <Play size={20} strokeWidth={2.5} fill="currentColor" />
                  </div>
                  <div>
                    <div className="label-mono text-white/50">— SOON ON</div>
                    <div className="font-display text-lg leading-tight">GOOGLE PLAY</div>
                  </div>
                </div>
                <span className="label-mono text-[#FFC107]/70">Q3 ’26</span>
              </div>
              <div className="border border-white/15 px-5 py-3.5 flex items-center justify-between hover:border-[#FFC107] transition-colors group cursor-not-allowed opacity-90" data-testid="store-appstore">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 border border-white/20 group-hover:border-[#FFC107] flex items-center justify-center text-[#FFC107]">
                    <Apple size={20} strokeWidth={2.2} fill="currentColor" />
                  </div>
                  <div>
                    <div className="label-mono text-white/50">— SOON ON</div>
                    <div className="font-display text-lg leading-tight">APP STORE</div>
                  </div>
                </div>
                <span className="label-mono text-[#FFC107]/70">Q3 ’26</span>
              </div>
            </div>
            <div className="mt-6 text-white/50 text-sm">
              Order & track your food anytime, anywhere.
            </div>
          </div>
        </div>

        {/* Massive brand */}
        <div className="mt-16 md:mt-24 select-none">
          <div className="font-display-tight text-[18vw] md:text-[16vw] leading-[0.85] uppercase text-white/95 break-all">
            WOODIES<span className="text-[#FF4500]">.</span>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-white/40 text-sm">
          <div>© ENQZONE OPC PRIVATE LIMITED</div>
          <div className="flex gap-5 flex-wrap">
            <a href="/legal/terms" data-testid="footer-terms" className="hover:text-white">Terms &amp; Conditions</a>
            <a href="/legal/privacy" data-testid="footer-privacy" className="hover:text-white">Privacy Policy</a>
            <a href="/legal" data-testid="footer-legal" className="hover:text-white">Legal</a>
            <a href="/blog" data-testid="footer-blog" className="hover:text-white">Blog</a>
            <a href="#story" className="hover:text-white">Story</a>
            <a href="#join" className="hover:text-white">Investors</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ============== ROOT ============== */
export default function Landing() {
  useEffect(() => {
    captureAttributionFromUrl();
  }, []);
  return (
    <div className="grain min-h-screen">
      <Toaster theme="dark" position="top-center" />
      <Nav />
      <Hero />
      <CityMarquee />
      <TractionStrip />
      <Story />
      <Menu />
      <FlavorStrip />
      <ImpactSection />
      <Join />
      <Footer />
    </div>
  );
}
